import React, { useState, useEffect, MouseEvent, ReactNode } from 'react';
import { ngContext } from '../../../core/services/ng.delegate';
import { NgContext } from '../../../../global';
import { Purl, PurlType } from './types';
import useSavedPurlsStore from './savedPurlsStore';
import useApiLogStore from './apiLogStore';
import PageTop from '../../../core/components/PageTop';

enum Render {
    Init = 'Init',
    Ready = 'Ready',
}

// context
let _ngCtx = {} as PosterContext;
type PosterContext = {
    isDev: boolean;
    isDebug: boolean;
    hasUser: boolean;
    isVerity: boolean;
    apiLog: boolean;
};
function contextVM(ctx: NgContext): PosterContext {
    console.log('poster contextVM, api: ' + ctx.apiLog);
    return {
        isDev: ctx.isDev,
        isDebug: ctx.isDebug,
        hasUser: !!ctx.User,
        isVerity: ctx.isVerity,
        apiLog: ctx.apiLog,
    };
}

// CONTROLLER
const AboutPosterCtl = (): JSX.Element => {
    // feature loads store data for child components
    const savedPurls = useSavedPurlsStore();
    const apiLog = useApiLogStore();

    const [render, setRender] = useState<Render>(Render.Init);

    // INIT
    useEffect(() => {
        console.log('POSTER: INIT');
        _ngCtx = contextVM(ngContext());
        if (!_ngCtx.hasUser) {
            // stop if no user
            return;
        }

        // verity user can access saved lists
        if (_ngCtx.isVerity || _ngCtx.isDev) {
            savedPurls.setList();
            console.log('poster checking apilog: ' + _ngCtx.apiLog);
            if (_ngCtx.apiLog) {
                apiLog.setList();
            }
        }

        setRender(Render.Ready);

        return destroy;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function destroy(): void {
        console.log('POSTER: destroy');
        _ngCtx = {} as PosterContext;

        // feature unloads store data
        savedPurls.unload();
        apiLog.unload();
    }

    // RENDER MAIN
    const showContent = render != Render.Init;
    return (
        <>
            <PageTop
                title="Poster"
                icon="icon-issue"
            />
            <div className="about-poster">{showContent && <Content />}</div>
        </>
    );
};

// CONTENT
const Content = (): JSX.Element => {
    return (
        <>
            <div className="top-row">
                <SavedPurls />
                {_ngCtx.isVerity || _ngCtx.isDev ? <ApiLog /> : null}
            </div>
            <div className="bottom-row">
                <PostAndResponse />
            </div>
        </>
    );
};

// SAVED PURLS
const SavedPurls = (): JSX.Element => {
    const savedPurls = useSavedPurlsStore();
    const purls = savedPurls.items;
    const activePath = savedPurls.activeItem.name;

    function handleTxtChange(e): void {
        // path textbox onChange user entry
        // tbd: local useState variable and throttling
        savedPurls.changeActiveProp(e.target.value, 'name');
    }

    function handleSetActive(purl: Purl): void {
        savedPurls.setActive(purl);
    }

    function handleDelete(name: string): void {
        savedPurls.remove(name);
    }

    function handleSave(purlType: PurlType): void {
        savedPurls.saveActive(purlType);
    }

    function handlePost(): void {
        savedPurls.postActive();
    }

    return (
        <div className="saved-purls">
            <h6>Saved & Mocked Purls</h6>
            {purls.map((purl, idx) => (
                <SavedPurlItem
                    key={idx}
                    purl={purl}
                    handleSetActive={handleSetActive}
                    handleDelete={handleDelete}
                />
            ))}
            <p>&nbsp;</p>
            <form className="query-form">
                <fieldset>
                    <div className="form-field input-txt">
                        <input
                            name="purlName"
                            type="text"
                            value={activePath}
                            onChange={handleTxtChange}
                            className="purl-input"
                        />
                        <span>
                            <Button onClick={handlePost}>POST</Button>
                        </span>
                        <span>
                            <Button
                                cssName="btn-alt"
                                onClick={() => handleSave(PurlType.SAVE)}
                            >
                                SAVE
                            </Button>
                        </span>
                        <span>
                            <Button
                                cssName="btn-alt"
                                onClick={() => handleSave(PurlType.MOCK)}
                            >
                                MOCK
                            </Button>
                        </span>
                    </div>
                </fieldset>
            </form>
        </div>
    );
};

type SavedPurlItemProps = {
    purl: Purl;
    handleSetActive: (p: Purl) => void;
    handleDelete: (name: string) => void;
};

const SavedPurlItem = ({ purl, handleSetActive, handleDelete }: SavedPurlItemProps): JSX.Element => {
    const title = purl.type == PurlType.SAVE ? 'SAVED' : 'MOCKED';

    return (
        <p className="purl-link">
            <button className="btn-off">{title}</button>
            <span>
                <Link onClick={() => handleSetActive(purl)}>{purl.name}</Link>
            </span>
            <span>
                <Link onClick={() => handleDelete(purl.name)}>delete</Link>
            </span>
        </p>
    );
};

// API LOG
const ApiLog = (): JSX.Element => {
    const apiLog = useApiLogStore();
    const savedPurls = useSavedPurlsStore();

    const logItems = apiLog.items;
    const toggleTitle = apiLog.isOn ? 'ON - Logging Last 10' : 'OFF - CLICK TO START';
    const toggleCSS = apiLog.isOn ? 'btn-alt' : '';

    useEffect(() => {
        _ngCtx.apiLog = apiLog.isOn; // update feature context, tbd global state
    }, [apiLog.isOn]);

    function handleToggle(): void {
        console.log('APILOG: toggle api recording');
        apiLog.toggle();
    }

    function handleChangePurl(purl: Purl): void {
        console.log('APILOG COMP: change purl to: ' + purl.name);
        savedPurls.setActive(purl);
    }

    return (
        <div className="api-log">
            <h6>
                Api Log
                <span>
                    <Button
                        onClick={handleToggle}
                        cssName={toggleCSS}
                    >
                        {toggleTitle}
                    </Button>
                </span>
            </h6>
            {logItems.map((logItem, idx) => (
                <ApiLogItem
                    key={idx}
                    logItem={logItem}
                    handleChangePurl={handleChangePurl}
                />
            ))}
        </div>
    );
};

type ApiLogItemProps = {
    logItem: Purl;
    handleChangePurl: (item: Purl) => void;
};

const ApiLogItem = ({ logItem, handleChangePurl }: ApiLogItemProps): JSX.Element => {
    logItem = logItem as Purl;
    const okStatus = logItem.status == '200';
    const statusCSS = okStatus ? 'icon-glyph icon-valid' : 'icon-glyph icon-remove';

    return (
        <p className="api-link">
            <span>
                <span className={statusCSS}></span>
            </span>
            <Link onClick={() => handleChangePurl(logItem)}>
                {!okStatus && <span>{logItem.status}</span>}
                <span>{logItem.name}</span>
            </Link>
        </p>
    );
};

// POST AND RESPONSE
const PostAndResponse = (): JSX.Element => {
    const savedPurls = useSavedPurlsStore();
    const purl = savedPurls.activeItem;

    function handleTxtChange(e, propName: string): void {
        savedPurls.changeActiveProp(e.target.value, propName);
    }

    return (
        <>
            <div className="post-payload">
                <p>Post Json: {purl.valid}</p>
                <textarea
                    name="purlJson"
                    value={purl.json}
                    onChange={e => handleTxtChange(e, 'json')}
                    spellCheck="false"
                    wrap="off"
                ></textarea>
            </div>
            <div className="post-resp">
                <p>Result: {purl.status}</p>
                <textarea
                    name="purlResp"
                    value={purl.response}
                    onChange={e => handleTxtChange(e, 'response')}
                    spellCheck="false"
                    wrap="off"
                ></textarea>
            </div>
        </>
    );
};

// HELPERS
type ClickableParams = {
    cssName?: string;
    onClick: () => void;
    children: ReactNode | string | undefined;
};
const Button = ({ cssName, onClick, children }: ClickableParams): JSX.Element => {
    function handleClick(e: MouseEvent): void {
        e.preventDefault();
        onClick();
    }
    return (
        <button
            type="button"
            className={cssName}
            onClick={handleClick}
        >
            {children}
        </button>
    );
};
const Link = ({ cssName, onClick, children }: ClickableParams): JSX.Element => {
    function handleClick(e: MouseEvent): void {
        e.preventDefault();
        onClick();
    }
    return (
        <a
            className={cssName}
            onClick={handleClick}
        >
            {children}
        </a>
    );
};

export default AboutPosterCtl;
